import React, { useEffect, useContext, useState, useRef } from 'react';
import { AppContext } from '../../../../context/Context';
import { useTranslation } from 'react-i18next';
import '../../../../assets/css/buttons.css';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material/';
import { Box, Button, Typography } from '@mui/material';

function ModalPapirGameBatchResult() {
  const { t } = useTranslation();

  const {
    kioskHall,
    updateFocusOnMainScreen,
    updateShowModalPapirGameBatchResult,
    papirScannedInfo,
    updatePapirScannedInfo,
    userInfo,
    parentInfo,
    operator,
    session,
    hall,
    updateIsLoadingScreen,
    updateTransactions,
    updateTransactionsArray,
    currentUser,
    updateCurrentUser,
    updateUserLogin,
    updateUserInfo,
    maximumAnonPrizeAllowed,
  } = useContext(AppContext);

  const kioskHallRef = useRef(kioskHall);
  useEffect(() => {
    kioskHallRef.current = kioskHall;
  }, [kioskHall]);

  const maximumAnonPrizeAllowedRef = useRef(maximumAnonPrizeAllowed);
  useEffect(() => {
    maximumAnonPrizeAllowedRef.current = maximumAnonPrizeAllowed;
  }, [maximumAnonPrizeAllowed]);

  const currentUserRef = useRef(currentUser);
  useEffect(() => {
    currentUserRef.current = currentUser;
  }, [currentUser]);

  const hallRef = useRef(hall);
  useEffect(() => {
    hallRef.current = hall;
  }, [hall]);

  const sessionRef = useRef(session);
  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  const operatorRef = useRef(operator);
  useEffect(() => {
    operatorRef.current = operator;
  }, [operator]);

  const userInfoRef = useRef(userInfo);
  useEffect(() => {
    userInfoRef.current = userInfo;
  }, [userInfo]);

  const parentInfoRef = useRef(parentInfo);
  useEffect(() => {
    parentInfoRef.current = parentInfo;
  }, [parentInfo]);

  const papirScannedInfoRef = useRef(papirScannedInfo);
  useEffect(() => {
    papirScannedInfoRef.current = papirScannedInfo;
  }, [papirScannedInfo]);

  const scrollRef = useRef(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    let lastY = 0;

    const onPointerDown = (e) => {
      el.setPointerCapture(e.pointerId);
      lastY = e.clientY;
    };

    const onPointerMove = (e) => {
      if (!el.hasPointerCapture(e.pointerId)) return;
      el.scrollTop += lastY - e.clientY;
      lastY = e.clientY;
    };

    const onPointerUp = (e) => {
      el.releasePointerCapture(e.pointerId);
    };

    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('pointermove', onPointerMove);
    el.addEventListener('pointerup', onPointerUp);
    el.addEventListener('pointercancel', onPointerUp);

    return () => {
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('pointermove', onPointerMove);
      el.removeEventListener('pointerup', onPointerUp);
      el.removeEventListener('pointercancel', onPointerUp);
    };
  }, []);

  //* DISABLE FOCUS ON MAIN SCREEN AND PREVENT ESCAPE BUTTON
  useEffect(() => {
    updateFocusOnMainScreen(false);

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  //* SOCKET SETUP

  const handleCancel = async () => {
    updatePapirScannedInfo(false);
    updateFocusOnMainScreen(true);
    updateShowModalPapirGameBatchResult(false);
  };

  return (
    <div id="react-confirm-alert">
      <div
        className="react-confirm-alert-overlay"
        style={{
          background: 'rgba(3,6,18,0.88)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div
          style={{
            width: 'min(860px, 94vw)',
            borderRadius: '24px',
            overflow: 'hidden',
            border: '1px solid rgba(255,255,255,0.12)',
            boxShadow: '0 30px 90px rgba(0,0,0,0.65)',
            background:
              'linear-gradient(135deg, rgba(18,26,55,0.98), rgba(10,14,28,0.98))',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              height: 70,
              px: '24px',
              display: 'flex',
              alignItems: 'center',
              borderBottom: '1px solid rgba(255,255,255,0.10)',
              background:
                'linear-gradient(135deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))',
              flexShrink: 0,
            }}
          >
            <Typography
              sx={{ color: 'white', fontSize: '24px', fontWeight: 900, lineHeight: 1 }}
            >
              {t('sales.papirBatchTitle')}
            </Typography>
          </Box>

          {/* Body */}
          <Box sx={{ p: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <Box
              sx={{
                borderRadius: '18px',
                border: '1px solid rgba(255,255,255,0.14)',
                background: 'rgba(255,255,255,0.06)',
                p: '24px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
              }}
            >
              <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: 22 }}>
                {kioskHallRef.current === ''
                  ? t('sales.papirBatchText1')
                  : t('sales.papirBatchText2')}
              </Typography>

              <Box
                ref={scrollRef}
                sx={{
                  maxHeight: '300px',
                  overflowY: 'scroll',
                  mt: '8px',
                  overscrollBehavior: 'contain',
                  touchAction: 'none',
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: 'rgba(255,255,255,0.06)',
                    borderRadius: '8px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#FFC727',
                    borderRadius: '8px',
                    '&:hover': {
                      background: '#FFD54F',
                    },
                  },
                }}
              >
                {papirScannedInfoRef.current?.length > 0 && (
                  <>
                    {papirScannedInfoRef.current.map((ticket, index) => (
                      <Typography
                        key={ticket.ticketID ?? index}
                        sx={{ color: 'rgba(255,255,255,0.75)', fontSize: 22, textAlign: 'center', py: '4px' }}
                      >
                        <Box component="span" sx={{ color: 'white', fontWeight: 700 }}>
                          {ticket.ticketID}
                        </Box>{' '}
                        {!ticket.isGameFinished
                          ? t('sales.papirBatchNotFinished')
                          : !ticket.hasTicketPrize
                            ? t('sales.papirBatchNotPrize')
                            : t('sales.papirBatchPrize') + ticket.win + ' NOK'}
                      </Typography>
                    ))}
                  </>
                )}
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: '14px' }}>
              <Button
                fullWidth
                variant="contained"
                onClick={() => { handleCancel(); }}
                startIcon={<CheckCircleIcon sx={{ fontSize: 28 }} />}
                sx={{
                  height: 76,
                  borderRadius: '16px',
                  bgcolor: '#FFC727',
                  color: '#000',
                  fontWeight: 800,
                  fontSize: 22,
                  textTransform: 'uppercase',
                  boxShadow: '0 12px 32px rgba(255,199,39,0.4)',
                  '&:hover': {
                    bgcolor: '#FFD54F',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 16px 40px rgba(255,199,39,0.55)',
                  },
                  '&:active': { transform: 'translateY(0)' },
                }}
              >
                {t('sales.confirm')}
              </Button>
            </Box>
          </Box>
        </div>
      </div>
    </div>
  );
}

export default ModalPapirGameBatchResult;
